import {Ref, UnwrapRef} from "vue";

export function usePromise<Type>(promise: Promise<Type>, defaultValue: Type): Ref<UnwrapRef<Type>> {
  const value = ref(defaultValue);
  promise.then((v: Type) => value.value = ref<Type>(v).value);

  return value;
}

export function times(i: number, start?: number): number[] {
  return [...Array(i)].map((v, i) => i + (start ?? 0));
}

export function waitFor(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function waitMinimumFor(ms: number, callback: () => Promise<any>) {
  return Promise.all([
    waitFor(ms),
    callback(),
  ]);
}

export async function nonblocking(callback: () => Promise<any>): Promise<any> {
  await new Promise((resolve) => {
    setTimeout(async () => resolve(await callback()), 0);
  });
}

export const tap = async function <T>(target: T, callback: (target: T) => any) {
  await callback(target);

  return target;
};

export const staticAs = function <T>(x: any): T {
  return ((x.constructor as unknown) as T);
};

export async function useInitial<T extends () => void>(refresh: T): Promise<{ refresh: T, refreshing: Ref<boolean> }> {
  const refreshing = ref(true);
  await refresh();
  refreshing.value = false;

  return {refresh, refreshing};
}

export function useAsyncInitial(refresh: () => Promise<any>): { refresh: () => Promise<any>, refreshing: Ref<boolean> } {
  const refreshing = ref(true);

  nonblocking(() => refresh().then(() => nextTick(() => refreshing.value = false)));

  return {
    refreshing,
    refresh: () => {
      refreshing.value = true;

      return nextTick(() => nonblocking(() => refresh().then(() => refreshing.value = false)));
    }
  };
}

export function useAsync<Type>(promise: Promise<Type>): Ref<Type | undefined> {
  const value = ref<Type>();

  onBeforeMount(() => {
    promise.then((v: Type) => value.value = v);
  });

  return value;
}

export function assertNumber(value: any): number {
  if (!value || typeof value !== 'string') {
    throw 'Invalid value';
  }

  return parseInt(value);
}

export function getRouteId(): number {
  const route = useRoute();

  return assertNumber(route.params.id);
}

export async function onRouteIdChange(cb: (id: number) => void | Promise<void>): Promise<void> {
  const route = useRoute();
  watch(() => route.params.id, id => {
    if (id !== undefined) {
      cb(assertNumber(id));
    }
  });

  await cb(getRouteId());
}

declare global {
  interface Array<T> {
    findAsync<T>(callback: (item: T, index: number) => Promise<boolean>): Promise<T | undefined>;

    forEachAsync<T>(callback: (item: T, index: number) => Promise<any>): Promise<this>;

    mapAsync<T, K>(callback: (item: T, index: number) => Promise<K>): Promise<K[]>;

    lastIndex(callback: (item: unknown, index: unknown) => boolean): unknown;

    count(callback: (item: unknown, index: unknown) => boolean): number;

    has(callback: (item: unknown, index: unknown) => boolean): boolean;
  }
}

Array.prototype.count = function (callback: (item: unknown, index: number) => boolean): number {
  return this.filter(callback).length;
};

Array.prototype.has = function (callback: (item: unknown, index: number) => boolean): boolean {
  return this.filter(callback).length > 0;
};

Array.prototype.lastIndex = function (callback: (item: unknown, index: number) => boolean): number {
  const index = collect(this).reverse().toArray().findIndex(callback);

  if (index === -1) {
    return index;
  }

  return this.length - 1 - index;
};

Array.prototype.findAsync = async function <T>(callback: (item: T, index: number) => Promise<boolean>): Promise<T | undefined> {
  let i = 0;
  for (const item of this) {
    if (await callback(item, i++)) {
      return item;
    }
  }

  return undefined;
};

Array.prototype.forEachAsync = async function <T>(callback: (item: T, index: number) => Promise<void>) {
  let i = 0;
  for (const item of this) {
    await callback(item, i++);
  }

  return this;
};

Array.prototype.mapAsync = async function <K, T>(callback: (item: T, index: number) => Promise<K>) {
  const result = [];
  let i = 0;
  for (const item of this) {
    result.push(await callback(item, i++));
  }

  return result;
};

